import { FormControl, Validators } from "@angular/forms";

export type FormFieldCommentType = "TEXT" | "WARNING" | "ALERT" | "INFO";

export const FORM_FIELD_COMMENT_TYPES: FormFieldCommentType[] = [
  "TEXT",
  "WARNING",
  "ALERT",
  "INFO",
];

export const FORM_FIELD_COMMENT_TYPE_ICONS: Record<FormFieldCommentType, string> = {
  TEXT: "chat_bubble_outline",
  WARNING: "warning_amber",
  ALERT: "report",
  INFO: "info_outline",
};

/**
 * A single info/warning/alert comment attached to a form field. Only one comment
 * per field is allowed (mirrors the mobile app's "field info hint" — see the
 * Field info hints screen in the mobile app).
 */
export class FormFieldComment {
  uuid?: string;
  /** Shown when the comment is folded. May be empty — no title is then displayed. */
  title?: string;
  body: string;
  type: FormFieldCommentType;
  defaultOpen: boolean;
  canFold: boolean;
  showIcon: boolean;

  constructor(data?: Partial<FormFieldComment>) {
    if (data) {
      Object.assign(this, data);
    }
    this.type = this.type ?? "INFO";
    this.defaultOpen = this.defaultOpen ?? true;
    this.canFold = this.canFold ?? true;
    this.showIcon = this.showIcon ?? true;
  }

  /**
   * Build a normalized comment from a raw parsed-JSON map — used both when a
   * saved template file is re-loaded (see FormsService.loadFormFromFile) and
   * when a form is fetched back from the API, so defaults stay consistent
   * regardless of source.
   */
  static fromMap(
    data: Record<string, unknown> | FormFieldComment | null | undefined,
  ): FormFieldComment | null {
    if (!data) {
      return null;
    }
    return new FormFieldComment({
      uuid: (data.uuid as string) ?? null,
      title: (data.title as string) || null,
      body: (data.body as string) ?? "",
      type: FORM_FIELD_COMMENT_TYPES.includes(
        data.type as FormFieldCommentType,
      )
        ? (data.type as FormFieldCommentType)
        : "INFO",
      defaultOpen: data.defaultOpen !== undefined ? !!data.defaultOpen : true,
      canFold: data.canFold !== undefined ? !!data.canFold : true,
      showIcon: data.showIcon !== undefined ? !!data.showIcon : true,
    });
  }

  toMap(comment: FormFieldComment): Map<string, unknown> {
    // No uuid: the exported file is imported on other systems as a new form.
    const map = new Map<string, unknown>();
    if (comment.title != null && comment.title !== "") {
      map.set("title", comment.title);
    }
    map.set("body", comment.body ?? "");
    map.set("type", comment.type ?? "INFO");
    map.set("defaultOpen", comment.defaultOpen ?? true);
    map.set("canFold", comment.canFold ?? true);
    map.set("showIcon", comment.showIcon ?? true);
    return map;
  }
}

export class FormFieldCommentForm {
  uuid = new FormControl<string | null>(null);
  title = new FormControl<string | null>(null);
  body = new FormControl<string | null>(null, [Validators.required]);
  type = new FormControl<FormFieldCommentType>("INFO", [Validators.required]);
  defaultOpen = new FormControl<boolean>(true);
  canFold = new FormControl<boolean>(true);
  showIcon = new FormControl<boolean>(true);

  constructor(comment?: Partial<FormFieldComment>) {
    const c = new FormFieldComment(comment ?? {});
    this.uuid.setValue(c.uuid ?? null);
    this.title.setValue(c.title ?? null);
    this.body.setValue(c.body ?? null);
    this.type.setValue(c.type);
    this.defaultOpen.setValue(c.defaultOpen);
    this.canFold.setValue(c.canFold);
    this.showIcon.setValue(c.showIcon);
  }
}
